import "./GenericDataGrid.scss"
import { DataGrid } from "devextreme-react";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import CustomStore from "devextreme/data/custom_store";
import React, { useRef, forwardRef, useImperativeHandle } from "react";
import { ODataFilterService } from "../../service/odataFilterService";
import { PagedList } from "../../api/pagedList";

export interface GenericDataGridProps {
    id: string
    className?: string
    rowKey: string
    handleAddNew?: () => void
    fetchData?: (odataQueryParameters?: string) => Promise<PagedList<object>>
    data?: object[]
    handleEdit?: (id: string) => void
    onOptionChanged?: any
    occupiedHeight?: number
    children: React.ReactNode
    columnAutoWidth?: boolean
    columnHidingEnabled?: boolean
    borderRadius?: "xsmall" | "small" | "medium"
}

export interface GenericDataGridRef {
    reload: () => void
}

export const GenericDataGrid = forwardRef<GenericDataGridRef, GenericDataGridProps>((props, ref) => {
    const dataGridRef = useRef<DataGridRef>(null)
    const pageSize = 25

    const store = (!!props.fetchData && !props.data) ?
        new CustomStore({
            key: props.rowKey,
            useDefaultSearch: false,
            async load(loadOptions) {
                try {
                    const odataQueryParameters = ODataFilterService.createQueryFilterParams(loadOptions)
                    const response = await props.fetchData!(odataQueryParameters)

                    return {
                        data: response.items,
                        totalCount: response.totalCount
                    }
                } catch (error) {
                    return {
                        data: [],
                        totalCount: 0
                    }
                }
            }
        }) : undefined

    useImperativeHandle(ref, () => ({
        reload: () => {
            dataGridRef.current?.instance?.().refresh()
        }
    }))

    return (
        <DataGrid
            className={`data-grid border-radius-${props.borderRadius ?? "medium"} ${props.className}`}
            ref={dataGridRef}
            dataSource={!props.data && props.fetchData ? store : props.data}
            onRowClick={e => props.handleEdit?.(e.key)}
            columnAutoWidth={props.columnAutoWidth ?? true}
            stateStoring={{ enabled: true, storageKey: `${props.id}`, type: "localStorage" }}
            paging={{ pageSize }}
            columnHidingEnabled={props.columnHidingEnabled ?? true}
            rowAlternationEnabled
            allowColumnResizing
            scrolling={{ showScrollbar: "always" }}
            remoteOperations={{ filtering: true, paging: true, sorting: true }}
            onOptionChanged={props.onOptionChanged}>
            {props.children}
        </DataGrid>
    );
})
